import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminInterface from './AdminInterface';
import ColorRecommendationDisplay from './ColorRecommendationDisplay';
import InventoryPanel from './InventoryPanel';
import SharesPanel from './SharesPanel';
import ReferenceSearchPage, { emptySearchState } from './ReferenceSearchPage';
import { identifyUser, trackEvent } from './analytics';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const onReferencesPage = location.pathname === '/references';

  // State management
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageId, setImageId] = useState(null);
  const [colorPalette, setColorPalette] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoverColor, setHoverColor] = useState(null);
  const [colorRecommendations, setColorRecommendations] = useState(null);
  const [selectedBrands, setSelectedBrands] = useState(['prismacolor_pencil']);
  const [inventoryOnly, setInventoryOnly] = useState(false);
  const [inventoryCount, setInventoryCount] = useState(0);
  const [inventoryVersion, setInventoryVersion] = useState(0);
  const [manualHex, setManualHex] = useState('');
  const [toasts, setToasts] = useState([]);
  const [selectedColorIndex, setSelectedColorIndex] = useState(null);
  const [colorRegions, setColorRegions] = useState([]);
  const [colorCoverage, setColorCoverage] = useState(0);
  const [connectors, setConnectors] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const [username, setUsername] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [googleInviteCode, setGoogleInviteCode] = useState('');
  const [userQuota, setUserQuota] = useState(0);
  const [userAccessLevel, setUserAccessLevel] = useState('none');
  const [showAdminInterface, setShowAdminInterface] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [shareVersion, setShareVersion] = useState(0);
  const [referenceSearchState, setReferenceSearchState] = useState(emptySearchState);
  const [canReturnToReferenceSearch, setCanReturnToReferenceSearch] = useState(false);

  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  const analysisRef = useRef(null);
  const swatchRefs = useRef({});
  const googleButtonRef = useRef(null);

  // Toast notification function
  const showToast = (message, type = 'info') => {
    const id = Date.now();
    const toast = { id, message, type };
    setToasts(prev => [...prev, toast]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Authentication functions
  const handleAuth = async (formData) => {
    setLoading(true);
    try {
      let endpoint, data;

      if (authMode === 'request') {
        endpoint = '/api/request-access';
        data = {
          username: formData.get('username'),
          email: formData.get('email'),
          reason: formData.get('reason')
        };
      } else {
        endpoint = authMode === 'login' ? '/api/login' : '/api/signup';
        data = {
          username: formData.get('username'),
          password: formData.get('password'),
          ...(authMode === 'signup' && {
            email: formData.get('email'),
            invite_code: formData.get('invite_code') || null
          })
        };
      }

      const response = await axios.post(`${API_URL}${endpoint}`, data);

      if (authMode === 'request') {
        showToast('Access request submitted! You will be notified if approved.', 'success');
        setShowAuthModal(false);
      } else {
        setUserToken(response.data.token);
        setUsername(response.data.username);
        setIsLoggedIn(true);
        setUserQuota(response.data.quota_remaining || 0);
        setUserAccessLevel(response.data.access_level || 'none');
        setShowAuthModal(false);

        localStorage.setItem('userToken', response.data.token);
        localStorage.setItem('username', response.data.username);

        identifyUser(response.data.username);
        trackEvent(authMode === 'login' ? 'login' : 'signup', {
          access_level: response.data.access_level || 'none',
        });

        showToast(`${authMode === 'login' ? 'Logged in' : 'Signed up'} successfully!`, 'success');
      }
    } catch (error) {
      console.error('Auth error:', error);

      let errorMessage = `${authMode} failed`;
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (typeof error.response.data.detail === 'object') {
          errorMessage = error.response.data.detail.msg || error.response.data.detail.message || 'Validation error';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
    }
    setLoading(false);
  };

  // Handle the ID token Google's Identity Services button hands back
  const handleGoogleCredential = useCallback(
    async (response) => {
      setLoading(true);
      try {
        const payload = { credential: response.credential };
        if (authMode === 'signup' && googleInviteCode) {
          payload.invite_code = googleInviteCode;
        }

        const apiResponse = await axios.post(`${API_URL}/api/auth/google`, payload);

        setUserToken(apiResponse.data.token);
        setUsername(apiResponse.data.username);
        setIsLoggedIn(true);
        setUserQuota(apiResponse.data.quota_remaining || 0);
        setUserAccessLevel(apiResponse.data.access_level || 'none');
        setShowAuthModal(false);

        localStorage.setItem('userToken', apiResponse.data.token);
        localStorage.setItem('username', apiResponse.data.username);

        identifyUser(apiResponse.data.username);
        trackEvent('login', { method: 'google', access_level: apiResponse.data.access_level || 'none' });

        showToast('Signed in with Google successfully!', 'success');
      } catch (error) {
        console.error('Google auth error:', error);

        let errorMessage = 'Google sign-in failed';
        if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
          } else if (typeof error.response.data.detail === 'object') {
            errorMessage = error.response.data.detail.msg || error.response.data.detail.message || 'Validation error';
          }
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        showToast(errorMessage, 'error');
      }
      setLoading(false);
    },
    [authMode, googleInviteCode]
  );

  // Render Google's button imperatively into googleButtonRef whenever the
  // relevant part of the auth modal is on screen. The declarative HTML
  // markup API (`data-client_id` etc.) only works if its target element
  // exists in the DOM before Google's script runs its one-time page scan —
  // not true here, since this div only mounts once a user opens the modal.
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    if (!showAuthModal || !(authMode === 'login' || authMode === 'signup')) return;
    if (!window.google?.accounts?.id || !googleButtonRef.current) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleGoogleCredential
    });

    googleButtonRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(googleButtonRef.current, {
      theme: 'outline',
      size: 'large',
      width: 320
    });
  }, [showAuthModal, authMode, handleGoogleCredential]);

  const handleLogout = () => {
    setUserToken(null);
    setUsername('');
    setIsLoggedIn(false);
    setInventoryOnly(false);
    setInventoryCount(0);
    localStorage.removeItem('userToken');
    localStorage.removeItem('username');
    identifyUser(null);
    showToast('Logged out successfully', 'success');
  };

  const handlePasswordReset = async (formData) => {
    setLoading(true);
    try {
      if (authMode === 'forgot') {
        await axios.post(`${API_URL}/api/request-password-reset`, {
          email: formData.get('email')
        });

        showToast('Password reset instructions sent! Check your email.', 'success');
        setAuthMode('login');
        showToast('Please check your email for reset instructions. In this demo, contact admin for password reset.', 'info');

      } else if (authMode === 'reset') {
        await axios.post(`${API_URL}/api/confirm-password-reset`, {
          reset_token: formData.get('reset_token') || resetToken,
          new_password: formData.get('new_password')
        });

        showToast('Password reset successful! You can now login with your new password.', 'success');
        setAuthMode('login');
        setResetToken(null);
      }
    } catch (error) {
      console.error('Password reset error:', error);

      let errorMessage = 'Password reset failed';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (typeof error.response.data.detail === 'object') {
          errorMessage = error.response.data.detail.msg || error.response.data.detail.message || 'Validation error';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
    }
    setLoading(false);
  };

  // Check for stored login on component mount
  React.useEffect(() => {
    const storedToken = localStorage.getItem('userToken');
    const storedUsername = localStorage.getItem('username');

    if (storedToken && storedUsername) {
      axios.get(`${API_URL}/api/verify-token?token=${storedToken}`)
        .then(response => {
          if (response.data.valid) {
            setUserToken(storedToken);
            setUsername(storedUsername);
            setIsLoggedIn(true);
            setUserQuota(response.data.quota_remaining || 0);
            setUserAccessLevel(response.data.access_level || 'none');
            identifyUser(storedUsername);
          } else {
            localStorage.removeItem('userToken');
            localStorage.removeItem('username');
          }
        })
        .catch(() => {
          localStorage.removeItem('userToken');
          localStorage.removeItem('username');
        });
    }
  }, []);

  const brands = [
    { id: 'general', name: 'General' },
    { id: 'prismacolor_pencil', name: 'Prismacolor Colored Pencil' },
    { id: 'stabilo_carbothello', name: 'Stabilo CarbOthello Pastel Pencil' },
    { id: 'sennelier_pastel', name: 'Sennelier Pastel Sticks' },
    { id: 'faber_castell_colored', name: 'Faber Castell Colored Pencil' },
    { id: 'faber_castell_pastel', name: 'Faber Castell Pastel Pencil' },
    { id: 'derwent_pastel', name: 'Derwent Pastel Pencil' },
    { id: 'kohinoor_gioconda', name: 'Koh-I-Noor Gioconda Pastel Pencil' },
    { id: 'conte_pastel', name: 'Conté à Paris Pastel Pencil' },
    { id: 'caran_dache_pastel', name: "Caran d'Ache Pastel Pencil" },
    { id: 'daniel_smith_watercolor', name: 'Daniel Smith Watercolor' },
    { id: 'holbein_watercolor', name: 'Holbein Watercolor' }
  ];

  // Brands grouped by medium, so you scan the column for what you actually own.
  // 'general' is deliberately outside the groups: it names a colour family
  // rather than a product, so it belongs to no medium.
  const brandGroups = [
    {
      title: 'Colored pencils',
      brandIds: ['prismacolor_pencil', 'faber_castell_colored']
    },
    {
      // Sennelier is the odd one out here: sticks, not pencils. Its label says so.
      title: 'Pastels',
      brandIds: [
        'stabilo_carbothello',
        'faber_castell_pastel',
        'derwent_pastel',
        'kohinoor_gioconda',
        'conte_pastel',
        'caran_dache_pastel',
        'sennelier_pastel'
      ]
    },
    {
      title: 'Watercolors',
      brandIds: ['daniel_smith_watercolor', 'holbein_watercolor']
    }
  ];

  // Bundled examples in /public/samples (see generate_sample_images.py)
  const sampleImages = [
    {
      file: 'sunset-landscape.jpg',
      title: 'Sunset landscape',
      blurb: 'A wide warm-to-cool range, from gold to deep violet'
    },
    {
      file: 'still-life.jpg',
      title: 'Fruit still life',
      blurb: 'Saturated local colour with strong light and shadow'
    },
    {
      file: 'botanical.jpg',
      title: 'Botanical study',
      blurb: 'Close-together greens plus one bright accent'
    }
  ];

  // Upload image to backend
  const uploadImage = async (file) => {
    if (!file) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = { 'Content-Type': 'multipart/form-data' };
      if (userToken) {
        headers.Authorization = `Bearer ${userToken}`;
      }

      const response = await axios.post(`${API_URL}/api/upload-image`, formData, { headers });

      setImageId(response.data.image_id);
      setColorPalette(response.data.colors || []);
      setImagePreview(response.data.preview);

      trackEvent('upload_image', {
        color_count: response.data.colors?.length || 0,
        authenticated: Boolean(userToken),
      });

      showToast(`Image uploaded successfully! Extracted ${response.data.colors?.length || 0} colors.`, 'success');
    } catch (error) {
      console.error('Upload error:', error);
      showToast('Failed to upload image: ' + (error.response?.data?.detail || error.message), 'error');
    }
    setLoading(false);
  };

  const handleReferenceAnalyzed = (data, searchState) => {
    setImageId(data.image_id);
    setColorPalette(data.colors || []);
    setImagePreview(data.preview);
    setSelectedColorIndex(null);
    setColorRecommendations(null);
    setColorRegions([]);
    setCanReturnToReferenceSearch(Boolean(searchState?.results?.images?.length));
    navigate('/');
    trackEvent('analyze_reference', {
      color_count: data.colors?.length || 0,
    });
    showToast(
      `Reference analyzed! Found ${data.colors?.length || 0} colors.`,
      'success'
    );
  };

  // Shared entry point for both the file picker and the sample images —
  // auto-processes the image immediately, no button to press.
  const loadImageFile = (file) => {
    setSelectedFile(file);
    setCanReturnToReferenceSearch(false);

    setImageId(null);
    setColorPalette([]);
    setHoverColor(null);
    setColorRecommendations(null);
    setSelectedColorIndex(null);
    setColorRegions([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    uploadImage(file);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      loadImageFile(file);
    }
  };

  // Pull a bundled sample out of /public and feed it through the same flow
  const loadSampleImage = async (sample) => {
    setLoading(true);
    try {
      const url = `${process.env.PUBLIC_URL || ''}/samples/${sample.file}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Could not load sample (${response.status})`);
      }
      const blob = await response.blob();
      loadImageFile(new File([blob], sample.file, { type: blob.type }));
    } catch (error) {
      console.error('Sample load error:', error);
      showToast(`Could not load the sample image: ${error.message}`, 'error');
      setLoading(false);
    }
  };

  // Copy the displayed preview into a hidden canvas for hover picking and region
  // markers. Use the same <img> element — do not set crossOrigin on data: or
  // blob: URLs or the browser may refuse to draw and every sample reads black.
  const syncCanvasFromImage = useCallback(() => {
    const img = imageRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas || !img.complete || img.naturalWidth === 0) return;

    const ctx = canvas.getContext('2d');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.drawImage(img, 0, 0);
  }, []);

  // Get color at mouse position
  const getColorAtPosition = useCallback((event) => {
    if (!imageRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = imageRef.current.getBoundingClientRect();

    const x = Math.floor((event.clientX - rect.left) * (canvas.width / rect.width));
    const y = Math.floor((event.clientY - rect.top) * (canvas.height / rect.height));

    try {
      const imageData = ctx.getImageData(x, y, 1, 1);
      const [r, g, b] = imageData.data;
      const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      setHoverColor({ hex, rgb: `rgb(${r}, ${g}, ${b})`, x: event.clientX, y: event.clientY });
    } catch (error) {
      console.error('Color picking error:', error);
      setHoverColor(null);
    }
  }, []);

  // Get color recommendations and map color regions
  const getColorRecommendations = async (colorHex, colorIndex) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/color-recommendations/${colorHex.replace('#', '')}`,
        {
          params: inventoryOnly
            ? { inventory_only: true }
            : { brand: selectedBrands.join(',') },
          headers: inventoryOnly ? { Authorization: `Bearer ${userToken}` } : {}
        }
      );
      setColorRecommendations(response.data);
      setSelectedColorIndex(colorIndex);

      if (colorIndex !== undefined && canvasRef.current && imageRef.current) {
        const { points, matchRatio } = findColorRegions(colorHex, colorIndex);
        setColorRegions(points);
        setColorCoverage(matchRatio);
      } else {
        setColorRegions([]);
        setColorCoverage(0);
      }
    } catch (error) {
      console.error('Color recommendation error:', error);
    }
  };

  // Create a public shareable link for the current result and copy it to the clipboard
  const createShare = async () => {
    if (!imageId) return;
    try {
      const brandsToShare = inventoryOnly ? ['inventory'] : selectedBrands;
      const response = await axios.post(
        `${API_URL}/api/shares`,
        { image_id: imageId, brands: brandsToShare },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      const shareUrl = response.data.share_url;
      setShareVersion((version) => version + 1);
      trackEvent('create_share', {
        brands: brandsToShare,
        inventory_only: inventoryOnly,
      });
      try {
        await navigator.clipboard.writeText(shareUrl);
        showToast(`Share link copied to clipboard: ${shareUrl}`, 'success');
      } catch (clipboardError) {
        showToast(`Share link created: ${shareUrl}`, 'success');
      }
    } catch (error) {
      console.error('Share creation error:', error);
      showToast('Failed to create share: ' + (error.response?.data?.detail || error.message), 'error');
    }
  };

  // Find where a color actually appears in the photo.
  // Returns a handful of spread-out hotspots rather than every matching pixel:
  // markers bunched inside one object do not show you where the color lives.
  const findColorRegions = (targetColor, colorIndex) => {
    if (!canvasRef.current) return { points: [], matchRatio: 0 };

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    const targetRgb = {
      r: parseInt(targetColor.slice(1, 3), 16),
      g: parseInt(targetColor.slice(3, 5), 16),
      b: parseInt(targetColor.slice(5, 7), 16)
    };

    const matches = [];
    const tolerance = 40;
    const step = 10;
    let sampled = 0;

    for (let y = 0; y < canvas.height; y += step) {
      for (let x = 0; x < canvas.width; x += step) {
        sampled += 1;
        const index = (y * canvas.width + x) * 4;
        const colorDistance = Math.sqrt(
          Math.pow(data[index] - targetRgb.r, 2) +
          Math.pow(data[index + 1] - targetRgb.g, 2) +
          Math.pow(data[index + 2] - targetRgb.b, 2)
        );

        if (colorDistance < tolerance) {
          matches.push({ x, y, distance: colorDistance });
        }
      }
    }

    // Closest matches first, then keep only those far enough apart to read as
    // separate places in the photo.
    matches.sort((a, b) => a.distance - b.distance);

    const minGap = Math.max(canvas.width, canvas.height) * 0.18;
    const points = [];

    for (const match of matches) {
      const isDistinct = points.every(
        (p) => Math.hypot(p.x - match.x, p.y - match.y) >= minGap
      );
      if (isDistinct) {
        points.push({ x: match.x, y: match.y, colorIndex });
      }
      if (points.length === 4) break;
    }

    return {
      points,
      matchRatio: sampled ? matches.length / sampled : 0
    };
  };

  // Re-sync canvas when the preview changes (onLoad handles the async decode).
  React.useEffect(() => {
    if (imagePreview) {
      syncCanvasFromImage();
    }
  }, [imagePreview, syncCanvasFromImage]);

  // Measure where the selected swatch and its matching spots actually sit on
  // screen, so the leader lines land on the photo instead of at a guessed edge.
  const measureConnectors = useCallback(() => {
    const container = analysisRef.current;
    const image = imageRef.current;
    const canvas = canvasRef.current;
    const swatch = swatchRefs.current[selectedColorIndex];

    if (!container || !image || !canvas || !swatch || colorRegions.length === 0) {
      setConnectors([]);
      return;
    }

    const containerBox = container.getBoundingClientRect();
    const swatchBox = swatch.getBoundingClientRect();
    const imageBox = image.getBoundingClientRect();

    const scaleX = imageBox.width / canvas.width;
    const scaleY = imageBox.height / canvas.height;

    const isLeftColumn = swatchBox.left < imageBox.left;
    const startX = (isLeftColumn ? swatchBox.right : swatchBox.left) - containerBox.left;
    const startY = swatchBox.top + swatchBox.height / 2 - containerBox.top;

    setConnectors(
      colorRegions.map((region) => ({
        x1: startX,
        y1: startY,
        x2: imageBox.left - containerBox.left + region.x * scaleX,
        y2: imageBox.top - containerBox.top + region.y * scaleY
      }))
    );
  }, [colorRegions, selectedColorIndex]);

  React.useLayoutEffect(() => {
    measureConnectors();
    window.addEventListener('resize', measureConnectors);
    return () => window.removeEventListener('resize', measureConnectors);
  }, [measureConnectors]);

  // A swatch plus its hex and product match. The swatch always sits on the side
  // nearest the photo so the leader line has the shortest run.
  const renderSwatch = (color, index, side) => (
    <div
      key={index}
      className={`flex items-center gap-2 ${side === 'left' ? 'flex-row-reverse' : 'flex-row'}`}
    >
      <button
        type="button"
        ref={(el) => { swatchRefs.current[index] = el; }}
        onClick={() => getColorRecommendations(color, index)}
        title={`Click to see where ${color} is in the photo`}
        aria-label={`Show where ${color} appears in the photo`}
        className={`w-16 h-12 shrink-0 rounded-lg shadow-md cursor-pointer transition-all hover:scale-105 focus:outline-none ${
          selectedColorIndex === index
            ? 'border-4 border-purple-600 ring-4 ring-purple-200 scale-105'
            : 'border-2 border-gray-200 hover:border-gray-400'
        }`}
        style={{ backgroundColor: color }}
      />
      <div className={`min-w-0 ${side === 'left' ? 'text-right' : 'text-left'}`}>
        <p className="text-xs text-gray-600 font-mono">{color}</p>
        <div className="text-xs text-purple-600 mt-0.5 break-words">
          <ColorRecommendationDisplay
            color={color}
            brands={selectedBrands}
            inventoryOnly={inventoryOnly}
            token={userToken}
            inventoryVersion={inventoryVersion}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <Link
              to="/"
              className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent"
            >
              Artist Helper Studio
            </Link>
            <div className="flex items-center space-x-4">
              <Link
                to="/references"
                title="Search stock photos for reference images"
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm transition-colors ${
                  onReferencesPage
                    ? 'bg-purple-100 text-purple-800'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Find Reference
              </Link>
              {isLoggedIn ? (
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">Welcome, {username}!</span>
                  {userAccessLevel === 'full' && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      Full Access
                    </span>
                  )}
                  {userAccessLevel === 'limited' && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                      Limited Access ({userQuota} uses left)
                    </span>
                  )}
                  {userAccessLevel === 'none' && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      Basic Access
                    </span>
                  )}
                  <button
                    onClick={() => setShowAdminInterface(true)}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    Admin
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Routes>
          <Route
            path="/references"
            element={
              <ReferenceSearchPage
                searchState={referenceSearchState}
                onSearchStateChange={setReferenceSearchState}
                onReferenceAnalyzed={handleReferenceAnalyzed}
                showToast={showToast}
                userToken={userToken}
              />
            }
          />
          <Route
            path="*"
            element={
        <div className="space-y-6">

          {canReturnToReferenceSearch && imagePreview && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-purple-900">
                Viewing colors from a reference photo
                {referenceSearchState.results?.query
                  ? ` (“${referenceSearchState.results.query}”)`
                  : ''}
                . Your search results are still available.
              </p>
              <Link
                to="/references"
                title="Return to reference photo search results"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back to reference search
              </Link>
            </div>
          )}

          {/* Intro — what this site is for. Hidden once an image is loaded. */}
          {!imagePreview && (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="max-w-3xl">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  Find the right pencils, pastels and paints for any photo
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Drawing from a reference photo and not sure which colours to reach for?
                  Give Artist Helper Studio your photo and it pulls out the colours the image is
                  actually built from, then tells you which products in <em>your</em> brands come
                  closest — by name, so you can pick them out of your tin or add them to a shopping list.
                </p>
                <p className="text-sm text-gray-500 mt-3">
                  Free to use, and no account needed — just add a photo below.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mt-8">
                {[
                  {
                    step: '1',
                    title: 'Add a photo',
                    body: 'Upload your own reference, or click one of the samples below. Analysis starts on its own — there is no button to press.',
                    icon: (
                      <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    )
                  },
                  {
                    step: '2',
                    title: 'Tick the brands you own',
                    body: 'Prismacolor, Stabilo CarbOthello, Sennelier, Faber-Castell, Daniel Smith, Holbein — pick as many as you like and matches are shown for each.',
                    icon: (
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )
                  },
                  {
                    step: '3',
                    title: 'Get named products',
                    body: 'Click any colour swatch for a main recommendation plus alternatives. Hover the photo to sample a colour anywhere, or type in a hex code.',
                    icon: (
                      <path d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    )
                  }
                ].map((item) => (
                  <div key={item.step} className="border border-gray-100 rounded-lg p-5 bg-gradient-to-br from-purple-50 to-white">
                    <div className="flex items-center mb-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-600 text-white text-sm font-bold mr-3">
                        {item.step}
                      </span>
                      <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                        {item.icon}
                      </svg>
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-500 mt-6 border-t border-gray-100 pt-4">
                A note on matching: exact colour matches barely exist in real supplies, and you do not need one.
                Treat every recommendation as "aim for this" and use the closest thing you already own.
              </p>
            </div>
          )}

          {isLoggedIn && (
            <InventoryPanel
              token={userToken}
              brands={brands}
              showToast={showToast}
              onInventoryChanged={(items) => {
                const usableCount = items.filter((item) => item.status !== 'wishlist').length;
                setInventoryCount(usableCount);
                setInventoryVersion((version) => version + 1);
                if (usableCount === 0) setInventoryOnly(false);
              }}
            />
          )}

          {isLoggedIn && (
            <SharesPanel
              token={userToken}
              showToast={showToast}
              refreshVersion={shareVersion}
            />
          )}

          {/* Image Upload Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Upload Your Image</h2>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="text-gray-500">
                  <svg className="mx-auto h-12 w-12 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p className="text-lg">Click to upload image</p>
                  <p className="text-sm text-gray-400">PNG, JPG, GIF up to 10MB</p>
                </div>
              </label>
            </div>

            {/* Sample images — one click loads them through the normal upload flow */}
            <div className="mt-6">
              <p className="text-sm font-medium text-gray-700">
                {imagePreview ? 'Or try one of these samples:' : "No photo handy? Try one of these:"}
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Each one is picked to show off a different kind of palette.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                {sampleImages.map((sample) => (
                  <button
                    key={sample.file}
                    type="button"
                    onClick={() => loadSampleImage(sample)}
                    disabled={loading}
                    className="group text-left rounded-lg overflow-hidden border border-gray-200 hover:border-purple-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all disabled:opacity-50"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL || ''}/samples/${sample.file}`}
                      alt={sample.title}
                      className="w-full h-32 object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="p-3">
                      <p className="text-sm font-semibold text-gray-800 group-hover:text-purple-700">
                        {sample.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{sample.blurb}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

          </div>

          {/* Image Preview & Color Picker */}
          {imagePreview && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Your Image with Color Analysis</h3>
              <p className="text-sm text-gray-600 mb-4">
                The swatches around your photo are the colours it is mostly made of, biggest area first.
                <span className="font-medium text-gray-700"> Click a swatch</span> to see which products match it and where that
                colour sits in the photo, or <span className="font-medium text-gray-700">hover anywhere on the image</span> to
                read off the colour under your cursor. Set your brands and look up a specific hex code below.
              </p>
              {/* The swatch columns are real flex children, not absolutely positioned:
                  with 20 colours and several brands per swatch they used to overflow a
                  fixed height and land on top of the note below. */}
              <div ref={analysisRef} className="relative flex items-center justify-center gap-2">

                {/* Left side color swatches */}
                {colorPalette.length > 0 && (
                  <div className="flex flex-col justify-center gap-3 w-40 shrink-0">
                    {colorPalette
                      .slice(0, Math.ceil(colorPalette.length / 2))
                      .map((color, index) => renderSwatch(color, index, 'left'))}
                  </div>
                )}

                {/* Center image container */}
                <div className="relative shrink-0">
                  <img
                    ref={imageRef}
                    src={imagePreview}
                    alt="Preview"
                    className="max-w-full h-auto rounded-lg shadow-md cursor-crosshair"
                    onMouseMove={getColorAtPosition}
                    onMouseLeave={() => setHoverColor(null)}
                    onLoad={() => {
                      syncCanvasFromImage();
                      measureConnectors();
                    }}
                    style={{ maxHeight: '400px', width: 'auto' }}
                  />
                  <canvas ref={canvasRef} className="hidden" />

                  {/* Knock the photo back slightly so the marked spots read first.
                      Kept light: any heavier and the photo just looks washed out. */}
                  {connectors.length > 0 && (
                    <div className="absolute inset-0 rounded-lg bg-white/20 pointer-events-none" />
                  )}

                  {/* Color picker tooltip */}
                  {hoverColor && (
                    <div
                      className="absolute pointer-events-none bg-black text-white px-2 py-1 rounded text-sm z-20"
                      style={{
                        left: `${hoverColor.x - (imageRef.current?.getBoundingClientRect().left || 0)}px`,
                        top: `${hoverColor.y - (imageRef.current?.getBoundingClientRect().top || 0) - 40}px`
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-4 h-4 rounded border border-white"
                          style={{ backgroundColor: hoverColor.hex }}
                        ></div>
                        <span>{hoverColor.hex}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right side color swatches */}
                {colorPalette.length > 1 && (
                  <div className="flex flex-col justify-center gap-3 w-40 shrink-0">
                    {colorPalette
                      .slice(Math.ceil(colorPalette.length / 2))
                      .map((color, index) =>
                        renderSwatch(color, index + Math.ceil(colorPalette.length / 2), 'right')
                      )}
                  </div>
                )}

                {/* Leader lines from the selected swatch to the spots in the photo
                    where that colour actually appears. Positions are measured from
                    the live DOM, so they stay correct at any image size. */}
                {connectors.length > 0 && (
                  <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
                    {connectors.map((line, idx) => (
                      <g key={idx}>
                        <line
                          x1={line.x1}
                          y1={line.y1}
                          x2={line.x2}
                          y2={line.y2}
                          stroke="#7c3aed"
                          strokeWidth="2"
                          strokeDasharray="5 4"
                          opacity="0.9"
                        />
                        {/* Target ring on the photo, filled with the colour it points at */}
                        <circle
                          cx={line.x2}
                          cy={line.y2}
                          r="11"
                          fill="none"
                          stroke="#ffffff"
                          strokeWidth="2"
                          opacity="0.9"
                        />
                        <circle
                          cx={line.x2}
                          cy={line.y2}
                          r="11"
                          fill={colorPalette[selectedColorIndex]}
                          stroke="#7c3aed"
                          strokeWidth="3"
                        />
                      </g>
                    ))}
                  </svg>
                )}
              </div>

              {/* Say what the lines mean, and how common the colour is */}
              {selectedColorIndex !== null && colorPalette[selectedColorIndex] && (
                <p className="text-sm text-center text-gray-600 mt-3">
                  {connectors.length > 0 ? (
                    <>
                      The dotted lines point to where{' '}
                      <span className="font-mono font-medium text-gray-800">
                        {colorPalette[selectedColorIndex]}
                      </span>{' '}
                      shows up in your photo — it covers roughly{' '}
                      <span className="font-medium text-gray-800">
                        {Math.max(1, Math.round(colorCoverage * 100))}%
                      </span>{' '}
                      of the image.
                    </>
                  ) : (
                    <>
                      Showing matches for{' '}
                      <span className="font-mono font-medium text-gray-800">
                        {colorPalette[selectedColorIndex]}
                      </span>.
                    </>
                  )}
                </p>
              )}

              {/* Educational message */}
              {colorPalette.length > 0 && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">
                    General colours to aim for - But use what you have in your supplies that's close
                  </h4>
                  <p className="text-xs text-blue-700">
                    Remember: exact colour matches are NOT required and you will almost never find an exact match.
                    Please use what you already have in your own supplies - close enough is good enough!
                  </p>
                </div>
              )}

              {/* Brand Selection, grouped by medium */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose your art supply brand(s) for color recommendations:
                </label>
                {isLoggedIn && (
                  <label className={`flex items-start gap-2 mb-3 p-3 rounded-lg border ${inventoryOnly ? 'bg-purple-50 border-purple-300' : 'bg-white border-gray-200'} ${inventoryCount === 0 ? 'opacity-60' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={inventoryOnly}
                      disabled={inventoryCount === 0}
                      onChange={(event) => setInventoryOnly(event.target.checked)}
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-800">Only match supplies I own</span>
                      <span className="block text-xs text-gray-500">
                        {inventoryCount > 0
                          ? `Compare against ${inventoryCount} owned or replacement-needed supplies.`
                          : 'Add an owned supply to your inventory to enable this mode.'}
                      </span>
                    </span>
                  </label>
                )}
                <div className={`grid md:grid-cols-3 gap-4 border border-gray-300 rounded-md p-4 ${inventoryOnly ? 'opacity-50' : ''}`}>
                  {brandGroups.map((group) => (
                    <div key={group.title}>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 pb-1 border-b border-gray-200">
                        {group.title}
                      </h4>
                      <div className="space-y-2">
                        {group.brandIds.map((brandId) => (
                          <label
                            key={brandId}
                            className="flex items-start text-sm text-gray-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              className="mr-2 mt-0.5"
                              disabled={inventoryOnly}
                              checked={selectedBrands.includes(brandId)}
                              onChange={(e) => {
                                setSelectedBrands((prev) => {
                                  if (e.target.checked) {
                                    return [...prev, brandId];
                                  }
                                  const next = prev.filter((id) => id !== brandId);
                                  return next.length > 0 ? next : prev;
                                });
                              }}
                            />
                            <span>{brands.find((b) => b.id === brandId)?.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <label className="flex items-center text-sm text-gray-600 mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mr-2"
                    disabled={inventoryOnly}
                    checked={selectedBrands.includes('general')}
                    onChange={(e) => {
                      setSelectedBrands((prev) => {
                        if (e.target.checked) {
                          return [...prev, 'general'];
                        }
                        const next = prev.filter((id) => id !== 'general');
                        return next.length > 0 ? next : prev;
                      });
                    }}
                  />
                  General — just name the colour family, no specific product
                </label>
              </div>

              {/* Manual color entry */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or type a hex color code:
                </label>
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const hex = manualHex.trim().replace('#', '');
                    if (/^[0-9a-fA-F]{6}$/.test(hex)) {
                      getColorRecommendations(`#${hex}`, undefined);
                    } else {
                      showToast('Enter a valid 6-character hex code, e.g. FF8800', 'error');
                    }
                  }}
                >
                  <input
                    type="text"
                    value={manualHex}
                    onChange={(e) => setManualHex(e.target.value)}
                    placeholder="#FF8800"
                    maxLength={7}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    Get Match
                  </button>
                </form>
              </div>

              {imageId && (
                <div className="mt-4">
                  <button
                    type="button"
                    disabled={!inventoryOnly && selectedBrands.length === 0}
                    onClick={() => {
                      if (!isLoggedIn) {
                        setAuthMode('login');
                        setShowAuthModal(true);
                        showToast('Log in to share your results.', 'info');
                        return;
                      }
                      createShare();
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Share this result
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    Creates a public link with this image and its matches. Anyone with the link can view it.
                    {!isLoggedIn && ' Log in first to share.'}
                    {!inventoryOnly && selectedBrands.length === 0 && ' Select at least one brand to share.'}
                  </p>
                </div>
              )}

              {/* Color Recommendations Display */}
              {colorRecommendations && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4 border">
                  <h4 className="font-medium text-gray-800 mb-2 flex items-center">
                    <div
                      className="w-6 h-6 rounded border mr-2"
                      style={{ backgroundColor: colorRecommendations.color }}
                    ></div>
                    Color Match for {colorRecommendations.color}
                  </h4>
                  <div className="space-y-3">
                    {colorRecommendations.recommendations
                      ? colorRecommendations.recommendations.map((rec, idx) => (
                          <div key={idx} className="space-y-1">
                            <p className="text-xs font-semibold text-gray-500 uppercase">
                              {brands.find((b) => b.id === rec.brand)?.name || rec.brand}
                            </p>
                            <div className="bg-white p-2 rounded border-l-4 border-green-400">
                              <p className="text-sm font-semibold text-green-800">Main Recommendation:</p>
                              <p className="text-sm text-gray-700">{rec.recommendation}</p>
                            </div>
                            {rec.alternatives && rec.alternatives.length > 0 && (
                              <div className="bg-white p-2 rounded border-l-4 border-blue-400">
                                <p className="text-sm font-semibold text-blue-800">Alternative Options:</p>
                                <ul className="text-sm text-gray-700 list-disc list-inside">
                                  {rec.alternatives.map((alt, altIdx) => (
                                    <li key={altIdx}>{alt}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))
                      : (
                        <>
                          <div className="bg-white p-2 rounded border-l-4 border-green-400">
                            <p className="text-sm font-semibold text-green-800">Main Recommendation:</p>
                            <p className="text-sm text-gray-700">{colorRecommendations.recommendation}</p>
                            {colorRecommendations.inventory_matches?.[0] && (
                              <p className="text-xs text-gray-500 mt-1">
                                {colorRecommendations.inventory_matches[0].brand} · {colorRecommendations.inventory_matches[0].match_quality} match · ΔE {colorRecommendations.inventory_matches[0].delta_e}
                              </p>
                            )}
                          </div>
                          {colorRecommendations.alternatives && colorRecommendations.alternatives.length > 0 && (
                            <div className="bg-white p-2 rounded border-l-4 border-blue-400">
                              <p className="text-sm font-semibold text-blue-800">Alternative Options:</p>
                              <ul className="text-sm text-gray-700 list-disc list-inside">
                                {colorRecommendations.alternatives.map((alt, idx) => (
                                  <li key={idx}>{alt}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
            }
          />
        </Routes>
      </div>

      {/* Authentication Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {authMode === 'login' ? 'Login' :
                 authMode === 'signup' ? 'Sign Up' :
                 authMode === 'request' ? 'Request Access' :
                 authMode === 'forgot' ? 'Forgot Password' :
                 authMode === 'reset' ? 'Reset Password' : 'Auth'}
              </h2>
              <button
                onClick={() => setShowAuthModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {process.env.REACT_APP_GOOGLE_CLIENT_ID &&
              (authMode === 'login' || authMode === 'signup') && (
                <div className="mb-6">
                  <div ref={googleButtonRef} className="flex justify-center" />
                  {authMode === 'signup' && (
                    <div className="mt-3">
                      <label
                        htmlFor="google_invite_code"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Invite Code for Google sign-in (optional)
                      </label>
                      <input
                        type="text"
                        id="google_invite_code"
                        value={googleInviteCode}
                        onChange={(e) => setGoogleInviteCode(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Enter invite code for AI access"
                      />
                    </div>
                  )}
                  <div className="flex items-center my-4">
                    <div className="flex-1 border-t border-gray-200"></div>
                    <span className="px-3 text-xs text-gray-400">OR</span>
                    <div className="flex-1 border-t border-gray-200"></div>
                  </div>
                </div>
              )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                if (authMode === 'forgot' || authMode === 'reset') {
                  handlePasswordReset(formData);
                } else {
                  handleAuth(formData);
                }
              }}
            >
              <div className="space-y-4">
                {(authMode === 'forgot') && (
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Enter your email address"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      We'll send password reset instructions to this email
                    </p>
                  </div>
                )}

                {(authMode === 'reset') && (
                  <>
                    <div>
                      <label htmlFor="reset_token" className="block text-sm font-medium text-gray-700 mb-2">
                        Reset Token
                      </label>
                      <input
                        type="text"
                        id="reset_token"
                        name="reset_token"
                        defaultValue={resetToken || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Enter reset token from email"
                      />
                    </div>
                    <div>
                      <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        id="new_password"
                        name="new_password"
                        required
                        minLength="6"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Enter your new password"
                      />
                    </div>
                  </>
                )}

                {!(authMode === 'forgot' || authMode === 'reset') && (
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Enter your username"
                    />
                  </div>
                )}

                {(authMode === 'signup' || authMode === 'request') && (
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Enter your email"
                    />
                  </div>
                )}

                {authMode === 'signup' && (
                  <div>
                    <label htmlFor="invite_code" className="block text-sm font-medium text-gray-700 mb-2">
                      Invite Code (optional)
                    </label>
                    <input
                      type="text"
                      id="invite_code"
                      name="invite_code"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Enter invite code for AI access"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Without an invite code, you'll have basic access only
                    </p>
                  </div>
                )}

                {authMode === 'request' && (
                  <div>
                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                      Why do you want access?
                    </label>
                    <textarea
                      id="reason"
                      name="reason"
                      required
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Tell us about your art projects..."
                    />
                  </div>
                )}

                {(authMode === 'login' || authMode === 'signup') && (
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      required
                      minLength="6"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Enter your password"
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 space-y-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Processing...' :
                   authMode === 'login' ? 'Login' :
                   authMode === 'signup' ? 'Sign Up' :
                   authMode === 'request' ? 'Submit Request' :
                   authMode === 'forgot' ? 'Send Reset Link' :
                   authMode === 'reset' ? 'Reset Password' : 'Submit'}
                </button>

                <div className="text-center space-y-2">
                  {authMode === 'login' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setAuthMode('signup')}
                        className="text-purple-600 hover:text-purple-800 text-sm block w-full"
                      >
                        Don't have an account? Sign up
                      </button>
                      <button
                        type="button"
                        onClick={() => setAuthMode('request')}
                        className="text-blue-600 hover:text-blue-800 text-sm block w-full"
                      >
                        Need access? Request here
                      </button>
                      <button
                        type="button"
                        onClick={() => setAuthMode('forgot')}
                        className="text-orange-600 hover:text-orange-800 text-sm block w-full"
                      >
                        Forgot password?
                      </button>
                    </>
                  )}
                  {authMode === 'signup' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setAuthMode('login')}
                        className="text-purple-600 hover:text-purple-800 text-sm block w-full"
                      >
                        Already have an account? Login
                      </button>
                      <button
                        type="button"
                        onClick={() => setAuthMode('request')}
                        className="text-blue-600 hover:text-blue-800 text-sm block w-full"
                      >
                        Don't have invite code? Request access
                      </button>
                    </>
                  )}
                  {authMode === 'request' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setAuthMode('login')}
                        className="text-purple-600 hover:text-purple-800 text-sm block w-full"
                      >
                        Already have account? Login
                      </button>
                      <button
                        type="button"
                        onClick={() => setAuthMode('signup')}
                        className="text-purple-600 hover:text-purple-800 text-sm block w-full"
                      >
                        Have invite code? Sign up
                      </button>
                    </>
                  )}
                  {(authMode === 'forgot' || authMode === 'reset') && (
                    <button
                      type="button"
                      onClick={() => setAuthMode('login')}
                      className="text-purple-600 hover:text-purple-800 text-sm block w-full"
                    >
                      Back to login
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`max-w-sm mx-auto bg-white border-l-4 p-4 rounded-lg shadow-lg transform transition-all duration-300 ease-in-out ${
              toast.type === 'success' ? 'border-green-400 bg-green-50' :
              toast.type === 'error' ? 'border-red-400 bg-red-50' :
              toast.type === 'warning' ? 'border-yellow-400 bg-yellow-50' :
              'border-blue-400 bg-blue-50'
            }`}
          >
            <div className="flex">
              <div className="flex-shrink-0">
                {toast.type === 'success' && (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
                {toast.type === 'error' && (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                {toast.type === 'warning' && (
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                {toast.type === 'info' && (
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-gray-700">{toast.message}</p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => removeToast(toast.id)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Admin Interface */}
      {showAdminInterface && (
        <AdminInterface
          userToken={userToken}
          onClose={() => setShowAdminInterface(false)}
        />
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="text-lg">Processing...</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
